import React, { useState, useEffect, useCallback } from 'react';
import './SliderManagement.css';

const SliderManagement = ({ adminPassword }) => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    image: '',
    title: '',
    subtitle: '',
    cta: 'Shop Now',
    order: 0,
    active: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchSlides = useCallback(async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/slider`, {
        headers: {
          'x-admin-password': adminPassword
        }
      });
      const data = await response.json();
      if (data.success) {
        setSlides(data.slides);
      }
    } catch (error) {
      console.error('Error fetching slides:', error);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/upload-image`, {
        method: 'POST',
        headers: {
          'x-admin-password': adminPassword
        },
        body: formDataUpload
      });

      const data = await response.json();
      if (data.success) {
        setFormData({ ...formData, image: data.imageUrl });
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image || !formData.title) {
      alert('Please provide at least an image and title');
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const url = editingSlide 
        ? `${apiUrl}/admin/slider/${editingSlide.id}`
        : `${apiUrl}/admin/slider`;
      
      const response = await fetch(url, {
        method: editingSlide ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': adminPassword
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        fetchSlides();
        resetForm();
      } else {
        alert(data.message || 'Error saving slide');
      }
    } catch (error) {
      console.error('Error saving slide:', error);
      alert('Error saving slide');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      image: slide.image,
      title: slide.title,
      subtitle: slide.subtitle || '',
      cta: slide.cta || 'Shop Now',
      order: slide.order || 0,
      active: slide.active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) {
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/slider/${slideId}`, {
        method: 'DELETE',
        headers: {
          'x-admin-password': adminPassword
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Slide deleted successfully');
        fetchSlides();
      } else {
        alert(data.message || 'Error deleting slide');
      }
    } catch (error) {
      console.error('Error deleting slide:', error);
      alert('Error deleting slide');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      image: '',
      title: '',
      subtitle: '',
      cta: 'Shop Now',
      order: 0,
      active: true
    });
    setEditingSlide(null);
    setShowForm(false);
  };

  return (
    <div className="slider-management">
      <div className="slider-management-header">
        <h2>Slider Management</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add New Slide'}
        </button>
      </div>

      {showForm && (
        <div className="slider-form-container">
          <h3>{editingSlide ? 'Edit Slide' : 'Add New Slide'}</h3>
          <form onSubmit={handleSubmit} className="slider-form">
            <div className="form-group">
              <label>Image *</label>
              <div className="image-upload-section">
                {formData.image && (
                  <div className="image-preview">
                    <img src={formData.image} alt="Preview" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                />
                {uploadingImage && <p>Uploading...</p>}
                <small>Or paste image URL below</small>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="Image URL"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., New Collection 2024"
                required
              />
            </div>

            <div className="form-group">
              <label>Subtitle</label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="e.g., Express Yourself with TRYON"
              />
            </div>

            <div className="form-group">
              <label>Button Text</label>
              <input
                type="text"
                value={formData.cta}
                onChange={(e) => setFormData({ ...formData, cta: e.target.value })}
                placeholder="e.g., Shop Now"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : editingSlide ? 'Update Slide' : 'Add Slide'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="slides-list">
        <h3>Current Slides ({slides.length})</h3>
        {loading && !showForm ? (
          <p>Loading...</p>
        ) : slides.length === 0 ? (
          <p>No slides found. Add your first slide!</p>
        ) : (
          <div className="slides-grid">
            {slides.map((slide) => (
              <div key={slide.id} className="slide-card">
                <div className="slide-image">
                  <img src={slide.image} alt={slide.title} />
                  <span className={`slide-status ${slide.active ? 'active' : 'inactive'}`}>
                    {slide.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="slide-info">
                  <h4>{slide.title}</h4>
                  <p>{slide.subtitle}</p>
                  <div className="slide-meta">
                    <span>Order: {slide.order}</span>
                    <span>CTA: {slide.cta}</span>
                  </div>
                  <div className="slide-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(slide)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(slide.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SliderManagement;
